import { useMemo, useState } from "react";
import { RANKED_PPP_PARAMS, usePpps } from "@/hooks/usePpps";
import { Ppp, PppType } from "@/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PPP_TYPE_DOT, PPP_TYPE_LABEL, PPP_TYPES } from "@/components/ppp/ppp-constants";
import { PppLock } from "@/components/ppp/PppLock";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Layers, Loader2, X } from "lucide-react";

interface PppMultiSelectProps {
  value: string[];
  onChange: (pids: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** `filter` narrows a list: compact trigger, no chip row. Default `link` attaches PPPs to a deliverable. */
  mode?: "link" | "filter";
}

/** Links a deliverable to PPPs. Ranked order from the server; grouped by type for scanning. */
export function PppMultiSelect({ value, onChange, disabled, placeholder = "Link to PPP…", className, mode = "link" }: PppMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = usePpps(RANKED_PPP_PARAMS);
  const ppps = useMemo(() => data?.data ?? [], [data?.data]);

  const groups = useMemo(
    () => PPP_TYPES.map((type) => ({ type, items: ppps.filter((p) => p.type === type) })).filter((g) => g.items.length > 0),
    [ppps],
  );
  const selected = ppps.filter((p) => value.includes(p.pid));
  const toggle = (pid: string) => onChange(value.includes(pid) ? value.filter((p) => p !== pid) : [...value, pid]);

  return (
    <div className={cn("space-y-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled} className="h-9 w-full justify-between text-sm font-normal">
            <span className={cn("flex min-w-0 items-center gap-2 truncate", value.length === 0 && "text-muted-foreground")}>
              <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              {value.length === 0
                ? placeholder
                : mode === "filter" && selected.length === 1
                  ? selected[0].name
                  : `${value.length} PPP${value.length === 1 ? "" : "s"} ${mode === "filter" ? "selected" : "linked"}`}
            </span>
            {isLoading ? <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin" /> : <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-40" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search PPPs…" />
            <CommandList className="max-h-72">
              <CommandEmpty>{isLoading ? "Loading…" : "No PPP found."}</CommandEmpty>
              {groups.map((g) => (
                <CommandGroup key={g.type} heading={PPP_TYPE_LABEL[g.type as PppType]}>
                  {g.items.map((p: Ppp) => (
                    <CommandItem key={p.pid} value={`${p.name} ${p.keyResults.map((kr) => kr.code).join(" ")}`} onSelect={() => toggle(p.pid)} className="gap-2">
                      <Check className={cn("h-3.5 w-3.5 shrink-0", value.includes(p.pid) ? "opacity-100" : "opacity-0")} />
                      <span className={cn("h-2 w-2 shrink-0 rounded-full", PPP_TYPE_DOT[p.type])} />
                      <span className="truncate">{p.name}</span>
                      {p.isConfidential && <PppLock />}
                      {p.keyResults.length > 0 && (
                        <span className="ml-auto shrink-0 font-mono text-[10px] text-muted-foreground">
                          {p.keyResults.slice(0, 2).map((kr) => kr.code).join(" ")}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {mode === "link" && selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((p) => (
            <Badge key={p.pid} variant="secondary" className="gap-1.5 pr-1 font-normal">
              <span className={cn("h-1.5 w-1.5 rounded-full", PPP_TYPE_DOT[p.type])} />
              <span className="max-w-[180px] truncate text-xs">{p.name}</span>
              {!disabled && (
                <button type="button" aria-label={`Unlink ${p.name}`} onClick={() => toggle(p.pid)} className="rounded-full p-0.5 hover:bg-muted-foreground/20">
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
